// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { BIP44_COIN_NANO, BIP44_PURPOSE, HARDENED_OFFSET, SLIP10_ED25519 } from './constants.js';
import { bytes, dec, hex, utf8 } from './convert.js';
/**
* Derives a private child key following the BIP-32 and BIP-44 derivation path
* registered to the Nano block lattice. Only hardened child keys are defined.
*
* @param {string} seed - Hexadecimal seed derived from mnemonic phrase
* @param {number} index - Account number between 0 and 2^31-1
* @returns Private child key for the account
*/
export async function nanoCKD(seed, index) {
    if (!Number.isSafeInteger(index) || index < 0 || index > 0x7fffffff) {
        throw new RangeError(`Invalid child key index 0x${index.toString(16)}`);
    }
    const masterKey = await slip10(SLIP10_ED25519, seed);
    const purposeKey = await CKDpriv(masterKey, BIP44_PURPOSE + HARDENED_OFFSET);
    const coinKey = await CKDpriv(purposeKey, BIP44_COIN_NANO + HARDENED_OFFSET);
    const accountKey = await CKDpriv(coinKey, index + HARDENED_OFFSET);
    return accountKey.privateKey;
}
async function slip10(curve, S) {
    const key = utf8.toBytes(curve);
    const data = hex.toBytes(S);
    const I = await hmac(key, data);
    const IL = I.slice(0, I.length / 2);
    const IR = I.slice(I.length / 2);
    return ({ privateKey: IL, chainCode: IR });
}
async function CKDpriv({ privateKey, chainCode }, index) {
    const key = hex.toBytes(chainCode);
    const data = hex.toBytes(`00${bytes.toHex(ser256(privateKey))}${bytes.toHex(ser32(index))}`);
    const I = await hmac(key, data);
    const IL = I.slice(0, I.length / 2);
    const IR = I.slice(I.length / 2);
    return ({ privateKey: IL, chainCode: IR });
}
function ser32(integer) {
    const bits = integer.toString(2);
    if (bits.length > 32) {
        throw new RangeError(`Expected 32-bit integer, received ${bits.length} bits: ${bits}`);
    }
    const bytes = dec.toBytes(integer);
    const result = new Uint8Array(4);
    result.set(bytes, 4 - bytes.length);
    return result;
}
function ser256(integer) {
    const bits = hex.toBin(integer);
    if (bits.length > 256) {
        throw new RangeError(`Expected 256-bit integer, received ${bits.length} bits: ${bits}`);
    }
    const bytes = hex.toBytes(integer);
    const result = new Uint8Array(32);
    result.set(bytes, 32 - bytes.length);
    return result;
}
async function hmac(key, data) {
    const { subtle } = globalThis.crypto;
    const pk = await subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-512' }, false, ['sign']);
    const signature = await subtle.sign('HMAC', pk, data);
    return bytes.toHex(new Uint8Array(signature));
}
